import React, { createContext, useState, useContext } from 'react';
import { CustomerInfo, ServiceConfig } from '../services/types';

export interface SelectedService {
  lineId: string;
  id: string;
  sku: string;
  location: string;
  description: string;
  quantity: number;
  serviceNote?: string;
  requiredInfoValues?: Record<string, string>;
}

export interface PhotoUploadInfo {
  photo: File | null;
  comment?: string;
  tag: string;
}

interface State {
  configs: ServiceConfig[] | null;
  setConfigs: React.Dispatch<React.SetStateAction<ServiceConfig[] | null>>;
  customer: CustomerInfo | null;
  setCustomer: React.Dispatch<React.SetStateAction<CustomerInfo | null>>;
  selectedServices: SelectedService[];
  setSelectedServices: React.Dispatch<React.SetStateAction<SelectedService[]>>;
  productId: string;
  storeId: string;
  extendedAttributes: { name?: string; value?: string }[];
  associateId: string | null;
  associateEmail: string | null;
  associateName: string | null;
  photoUploadInfo: PhotoUploadInfo;
  setPhotoUploadInfo: (info: PhotoUploadInfo) => void;
  serviceType: string | null;
  setServiceType: React.Dispatch<React.SetStateAction<string | null>>;
}

const ServiceConfigContext = createContext<State | undefined>(undefined);

export const ServiceConfigProvider: React.FC<{
  children: React.ReactNode;
  productId: string;
  storeId: string;
  extendedAttributes?: { name?: string; value?: string }[];
  associateId?: string | null;
  associateEmail?: string | null;
  associateName?: string | null;
}> = ({ children, productId, storeId, extendedAttributes = [], associateId = null, associateEmail = null, associateName = null }) => {
  const [configs, setConfigs] = useState<ServiceConfig[] | null>(null);
  const [customer, setCustomer] = useState<CustomerInfo | null>(null);
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>([]);
  const [photoUploadInfo, setPhotoUploadInfo] = useState<PhotoUploadInfo>({
    photo: null,
    comment: '',
    tag: '',
  });
  const [serviceType, setServiceType] = useState<string | null>(null);

  return (
    <ServiceConfigContext.Provider
      value={{
        configs,
        setConfigs,
        customer,
        setCustomer,
        selectedServices,
        setSelectedServices,
        productId,
        storeId,
        extendedAttributes,
        associateId,
        associateEmail,
        associateName,
        photoUploadInfo,
        setPhotoUploadInfo,
        serviceType,
        setServiceType,
      }}
    >
      {children}
    </ServiceConfigContext.Provider>
  );
};

export const useServiceConfigs = () => {
  const ctx = useContext(ServiceConfigContext);
  if (!ctx) throw new Error('useServiceConfigs must be used inside ServiceConfigProvider');
  return ctx;
};
